import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Megaphone, Trash2 } from 'lucide-react';
import api from '../lib/axios';
import { euro, dateFmt } from '../lib/format';

type CampaignKind = 'DONATION' | 'MEMBERSHIP' | 'EVENT' | 'GENERAL';
type CampaignStatus = 'ACTIVE' | 'CLOSED' | 'ARCHIVED';

interface Campaign {
  id: number;
  label: string;
  description: string | null;
  kind: CampaignKind;
  accountNumber: string;
  expenseAccountNumber: string | null;
  startDate: string | null;
  endDate: string | null;
  targetAmount: number | null;
  collectedAmount: number | null;
  status: CampaignStatus;
}

type CampaignForm = {
  label: string;
  description: string;
  kind: CampaignKind;
  accountNumber: string;
  expenseAccountNumber: string;
  startDate: string;
  endDate: string;
  targetAmount: string;
};

const EMPTY: CampaignForm = {
  label: '',
  description: '',
  kind: 'DONATION',
  accountNumber: '',
  expenseAccountNumber: '',
  startDate: '',
  endDate: '',
  targetAmount: '',
};

const KINDS: CampaignKind[] = ['DONATION', 'MEMBERSHIP', 'EVENT', 'GENERAL'];

function today() {
  return new Date().toISOString().slice(0, 10);
}

export default function Campaigns() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState<CampaignForm>({ ...EMPTY });
  const [error, setError] = useState<string | null>(null);
  const [detailId, setDetailId] = useState<number | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<Campaign[]>('/campaigns').then((r) => r.data),
  });

  const accounts = useQuery({
    queryKey: ['accounts'],
    queryFn: () => api.get('/accounts').then((r) => r.data),
  });

  // Income accounts (class 7) fund donations/memberships; class 6 accounts book event expenses.
  const incomeAccounts = (accounts.data ?? []).filter(
    (a: any) => a.accountClass === 7 && a.nature === 'DETAIL' && a.active,
  );
  const expenseAccounts = (accounts.data ?? []).filter(
    (a: any) => a.accountClass === 6 && a.nature === 'DETAIL' && a.active,
  );

  const save = useMutation({
    mutationFn: () => {
      const body = {
        label: form.label.trim(),
        description: form.description.trim() || null,
        kind: form.kind,
        accountNumber: form.accountNumber.trim(),
        expenseAccountNumber:
          form.kind === 'EVENT' && form.expenseAccountNumber ? form.expenseAccountNumber : null,
        parentId: null,
        startDate: form.startDate || null,
        endDate: form.endDate || null,
        targetAmount: form.targetAmount ? Number(form.targetAmount) : null,
      };
      return editingId != null
        ? api.put(`/campaigns/${editingId}`, body).then((r) => r.data)
        : api.post('/campaigns', body).then((r) => r.data);
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['campaigns'] });
      resetForm();
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('campaigns.saveError')),
  });

  const close = useMutation({
    mutationFn: (id: number) => api.post(`/campaigns/${id}/close`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
  });

  const remove = useMutation({
    mutationFn: (id: number) => api.delete(`/campaigns/${id}`).then((r) => r.data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['campaigns'] }),
    onError: (e: any) => window.alert(e?.response?.data?.message ?? t('campaigns.deleteError')),
  });

  const resetForm = () => {
    setShowForm(false);
    setEditingId(null);
    setForm({ ...EMPTY });
    setError(null);
  };

  const edit = (c: Campaign) => {
    setEditingId(c.id);
    setForm({
      label: c.label,
      description: c.description ?? '',
      kind: c.kind,
      accountNumber: c.accountNumber,
      expenseAccountNumber: c.expenseAccountNumber ?? '',
      startDate: c.startDate ?? '',
      endDate: c.endDate ?? '',
      targetAmount: c.targetAmount != null ? String(c.targetAmount) : '',
    });
    setShowForm(true);
    setError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Quick sub-item removed: campaigns are a flat list.

  const submit = () => {
    setError(null);
    if (!form.label.trim() || !form.accountNumber.trim()) {
      setError(t('campaigns.missingFields'));
      return;
    }
    save.mutate();
  };

  const campaigns = data ?? [];
  const detailCampaign = campaigns.find((c) => c.id === detailId) ?? null;

  return (
    <div>
      <div className="mb-6 flex items-end justify-between gap-4">
        <div>
          <p className="eyebrow">{t('nav.campaigns')}</p>
          <h1 className="page-title mt-1">{t('campaigns.title')}</h1>
          <p className="mt-1 text-sm text-ink-muted">{t('campaigns.subtitle')}</p>
        </div>
        <button
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          className={showForm ? 'btn btn-secondary' : 'btn btn-primary'}
        >
          {showForm ? (
            t('common.close')
          ) : (
            <>
              <Plus size={16} /> {t('campaigns.new')}
            </>
          )}
        </button>
      </div>

      {showForm && (
        <div className="reveal card mb-6 p-6">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <div className="md:col-span-2">
              <label className="field-label">{t('campaigns.label')}</label>
              <input
                value={form.label}
                onChange={(e) => setForm((f) => ({ ...f, label: e.target.value }))}
                className="input"
                placeholder={t('campaigns.labelPlaceholder')}
              />
            </div>
            <div>
              <label className="field-label">{t('campaigns.kind')}</label>
              <select
                value={form.kind}
                onChange={(e) => setForm((f) => ({ ...f, kind: e.target.value as CampaignKind }))}
                className="select"
              >
                {KINDS.map((k) => (
                  <option key={k} value={k}>
                    {t(`campaigns.kind${k}`)}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">{t('campaigns.account')}</label>
              <select
                value={form.accountNumber}
                onChange={(e) => setForm((f) => ({ ...f, accountNumber: e.target.value }))}
                className="select"
              >
                <option value="">{t('common.none')}</option>
                {incomeAccounts.map((a: any) => (
                  <option key={a.number} value={a.number}>
                    {a.number} - {a.label}
                  </option>
                ))}
              </select>
            </div>
            {form.kind === 'EVENT' && (
              <div>
                <label className="field-label">{t('campaigns.expenseAccount')}</label>
                <select
                  value={form.expenseAccountNumber}
                  onChange={(e) =>
                    setForm((f) => ({ ...f, expenseAccountNumber: e.target.value }))
                  }
                  className="select"
                >
                  <option value="">{t('campaigns.noExpenseAccount')}</option>
                  {expenseAccounts.map((a: any) => (
                    <option key={a.number} value={a.number}>
                      {a.number} - {a.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="field-label">{t('campaigns.startDate')}</label>
              <input
                type="date"
                value={form.startDate}
                onChange={(e) => setForm((f) => ({ ...f, startDate: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('campaigns.endDate')}</label>
              <input
                type="date"
                value={form.endDate}
                onChange={(e) => setForm((f) => ({ ...f, endDate: e.target.value }))}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('campaigns.target')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.targetAmount}
                onChange={(e) => setForm((f) => ({ ...f, targetAmount: e.target.value }))}
                className="input text-right"
                placeholder="0,00"
              />
            </div>
            <div className="md:col-span-2">
              <label className="field-label">{t('campaigns.description')}</label>
              <textarea
                value={form.description}
                onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                className="input"
                rows={2}
              />
            </div>
          </div>

          <div className="mt-5 flex items-center gap-3">
            <div className="flex-1" />
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
            <button onClick={submit} disabled={save.isPending} className="btn btn-primary">
              {save.isPending
                ? t('campaigns.creating')
                : editingId != null
                ? t('campaigns.update')
                : t('campaigns.create')}
            </button>
          </div>
        </div>
      )}

      {isLoading && <p className="text-ink-muted">{t('common.loading')}</p>}
      {!isLoading && campaigns.length === 0 && (
        <div className="card p-8 text-center text-ink-muted">{t('campaigns.empty')}</div>
      )}

      {campaigns.length > 0 && (
        <div className="card overflow-hidden">
          <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('campaigns.label')}</th>
                <th>{t('campaigns.kind')}</th>
                <th>{t('campaigns.account')}</th>
                <th className="num">{t('campaigns.collected')}</th>
                <th className="num">{t('campaigns.target')}</th>
                <th>{t('field.status')}</th>
                <th className="text-right">{t('field.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c) => (
                <tr
                  key={c.id}
                  onClick={() => setDetailId(c.id)}
                  className="cursor-pointer hover:bg-surface-raised"
                >
                  <td data-label={t('campaigns.label')}>
                    <span className="flex items-center gap-2 font-medium text-ink">
                      <Megaphone size={16} className="text-ink-muted" />
                      {c.label}
                    </span>
                    {(c.startDate || c.endDate) && (
                      <span className="ml-6 block text-xs text-ink-muted">
                        {c.startDate ? dateFmt(c.startDate) : '…'} →{' '}
                        {c.endDate ? dateFmt(c.endDate) : '…'}
                      </span>
                    )}
                  </td>
                  <td data-label={t('campaigns.kind')} className="text-ink-soft">{t(`campaigns.kind${c.kind}`)}</td>
                  <td data-label={t('campaigns.account')} className="tnum text-ink-soft">
                    {c.accountNumber}
                    {c.kind === 'EVENT' && c.expenseAccountNumber && (
                      <span className="block text-xs text-expense">
                        − {c.expenseAccountNumber}
                      </span>
                    )}
                  </td>
                  <td data-label={t('campaigns.collected')} className="num font-medium">{euro(c.collectedAmount ?? 0)}</td>
                  <td data-label={t('campaigns.target')} className="num text-ink-soft">
                    {c.targetAmount != null ? euro(c.targetAmount) : t('common.none')}
                  </td>
                  <td data-label={t('field.status')}>
                    <span
                      className={c.status === 'ACTIVE' ? 'pill pill-posted' : 'pill pill-neutral'}
                    >
                      {t(`campaigns.status${c.status}`)}
                    </span>
                  </td>
                  <td
                    onClick={(e) => e.stopPropagation()}
                    className="cell-actions text-right whitespace-nowrap"
                  >
                    <button onClick={() => edit(c)} className="btn btn-ghost !px-2">
                      {t('common.edit')}
                    </button>
                    {c.status === 'ACTIVE' && (
                      <button onClick={() => close.mutate(c.id)} className="btn btn-ghost !px-2">
                        {t('campaigns.close')}
                      </button>
                    )}
                    <button
                      onClick={() => {
                        if (window.confirm(t('campaigns.confirmDelete'))) remove.mutate(c.id);
                      }}
                      className="btn btn-ghost !px-2 text-expense"
                      title={t('common.delete')}
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}

      {detailCampaign && (
        <CampaignDetail
          campaign={detailCampaign}
          incomeAccounts={incomeAccounts}
          expenseAccounts={expenseAccounts}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  );
}

type Movement = { kind: 'INCOME' | 'EXPENSE'; account: string };

function CampaignDetail({
  campaign,
  incomeAccounts,
  expenseAccounts,
  onClose,
}: {
  campaign: Campaign;
  incomeAccounts: any[];
  expenseAccounts: any[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const membership = campaign.kind === 'MEMBERSHIP';

  const entries = useQuery({
    queryKey: ['campaign-entries', campaign.id],
    queryFn: () => api.get(`/campaigns/${campaign.id}/entries`).then((r) => r.data),
  });

  const contacts = useQuery({
    queryKey: ['contacts'],
    queryFn: () => api.get('/contacts').then((r) => r.data),
  });

  const [move, setMove] = useState<Movement>({ kind: 'INCOME', account: '' });
  const [date, setDate] = useState(today());
  const [amount, setAmount] = useState('');
  const [contactId, setContactId] = useState('');
  const [label, setLabel] = useState('');
  const [payment, setPayment] = useState<'BANK' | 'CASH' | 'CHECK'>('BANK');
  const [error, setError] = useState<string | null>(null);

  const add = useMutation({
    mutationFn: () => {
      const contact = (contacts.data ?? []).find((c: any) => String(c.id) === contactId);
      const body = {
        kind: membership ? 'INCOME' : move.kind,
        date,
        amount: Number(amount),
        contactId: contactId ? Number(contactId) : null,
        contactLabel: contact ? contact.displayName : null,
        paymentMethod: payment,
        account: membership ? null : move.account || null,
        label: label.trim() || null,
      };
      return api.post(`/campaigns/${campaign.id}/entries`, body).then((r) => r.data);
    },
    onSuccess: () => {
      setAmount('');
      setContactId('');
      setLabel('');
      setError(null);
      qc.invalidateQueries({ queryKey: ['campaign-entries', campaign.id] });
      qc.invalidateQueries({ queryKey: ['campaigns'] });
    },
    onError: (e: any) => setError(e?.response?.data?.message ?? t('campaigns.entryError')),
  });

  const submit = () => {
    setError(null);
    if (!amount || Number(amount) <= 0) {
      setError(t('campaigns.entryMissing'));
      return;
    }
    if (membership && !contactId) {
      setError(t('campaigns.contributorRequired'));
      return;
    }
    add.mutate();
  };

  const rows = entries.data ?? [];
  const accountOptions = move.kind === 'EXPENSE' ? expenseAccounts : incomeAccounts;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="card w-full max-w-3xl max-h-[90vh] overflow-y-auto p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <p className="eyebrow">{t(`campaigns.kind${campaign.kind}`)}</p>
            <h2 className="page-title text-xl">{campaign.label}</h2>
            <p className="mt-1 text-sm text-ink-muted">
              {t('campaigns.collected')} : {euro(campaign.collectedAmount ?? 0)}
              {campaign.targetAmount != null && ` / ${euro(campaign.targetAmount)}`}
            </p>
          </div>
          <button onClick={onClose} className="btn btn-ghost">
            {t('common.close')}
          </button>
        </div>

        <div className="rounded-lg bg-surface-raised p-4">
          <h3 className="mb-3 font-medium">
            {membership ? t('campaigns.addContributor') : t('campaigns.addMovement')}
          </h3>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
            {!membership && (
              <div>
                <label className="field-label">{t('campaigns.movementKind')}</label>
                <select
                  value={move.kind}
                  onChange={(e) =>
                    setMove({ kind: e.target.value as Movement['kind'], account: '' })
                  }
                  className="select"
                >
                  <option value="INCOME">{t('entries.income')}</option>
                  <option value="EXPENSE">{t('entries.expense')}</option>
                </select>
              </div>
            )}
            {membership ? (
              <div>
                <label className="field-label">{t('campaigns.contributor')}</label>
                <select
                  value={contactId}
                  onChange={(e) => setContactId(e.target.value)}
                  className="select"
                >
                  <option value="">{t('common.none')}</option>
                  {(contacts.data ?? []).map((c: any) => (
                    <option key={c.id} value={c.id}>
                      {c.displayName}
                    </option>
                  ))}
                </select>
              </div>
            ) : (
              <div>
                <label className="field-label">{t('campaigns.account')}</label>
                <select
                  value={move.account}
                  onChange={(e) => setMove((m) => ({ ...m, account: e.target.value }))}
                  className="select"
                >
                  <option value="">{t('campaigns.defaultAccount')}</option>
                  {accountOptions.map((a: any) => (
                    <option key={a.number} value={a.number}>
                      {a.number} - {a.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <div>
              <label className="field-label">{t('field.date')}</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="input"
              />
            </div>
            <div>
              <label className="field-label">{t('field.amount')}</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                className="input text-right"
                placeholder="0,00"
              />
            </div>
            <div>
              <label className="field-label">{t('campaigns.paymentMethod')}</label>
              <select
                value={payment}
                onChange={(e) => setPayment(e.target.value as 'BANK' | 'CASH' | 'CHECK')}
                className="select"
              >
                <option value="BANK">{t('campaigns.payBank')}</option>
                <option value="CASH">{t('campaigns.payCash')}</option>
                <option value="CHECK">{t('campaigns.payCheck')}</option>
              </select>
            </div>
            <div className="sm:col-span-2">
              <label className="field-label">{t('field.label')}</label>
              <input
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                className="input"
                placeholder={t('campaigns.entryLabelPlaceholder')}
              />
            </div>
          </div>
          <div className="mt-4 flex items-center gap-3">
            <div className="flex-1" />
            {error && <span className="text-sm font-medium text-expense">{error}</span>}
            <button onClick={submit} disabled={add.isPending} className="btn btn-primary">
              {t('common.add')}
            </button>
          </div>
        </div>

        <h3 className="mb-2 mt-6 font-medium">{t('campaigns.linkedEntries')}</h3>
        <div className="overflow-x-auto">
          <table className="table">
            <thead>
              <tr>
                <th>{t('field.date')}</th>
                <th>{t('field.number')}</th>
                <th>{t('field.label')}</th>
                <th className="num">{t('field.debit')}</th>
                <th className="num">{t('field.credit')}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((e: any) => (
                <tr key={e.id}>
                  <td data-label={t('field.date')} className="whitespace-nowrap text-ink-soft">{dateFmt(e.entryDate)}</td>
                  <td data-label={t('field.number')} className="tnum text-ink-soft">{e.number}</td>
                  <td data-label={t('field.label')} className="font-medium">{e.label}</td>
                  <td data-label={t('field.debit')} className="num text-ink-soft">{euro(e.totalDebit)}</td>
                  <td data-label={t('field.credit')} className="num text-ink-soft">{euro(e.totalCredit)}</td>
                </tr>
              ))}
              {rows.length === 0 && (
                <tr>
                  <td colSpan={5} className="py-6 text-center text-ink-muted">
                    {t('campaigns.noEntries')}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
